import { useEffect, useMemo, useRef, useState } from 'react'
import Paper from '@/components/Paper'
import PieChart, { PieChartDataItem } from '@/components/Analytic/PieChart'
import { SCHEMES } from '@/components/EchartCustomized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { SalesTargetFilters } from '@/modules/Report/components/BusinessTargets/types'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { getProductGroupReport } from '@/modules/Report/components/ServiceMonthlyReport/services'

interface ServicesSalesContributionsProps {
    filters: SalesTargetFilters
}

export default function ServicesSalesContributions({
    filters,
}: ServicesSalesContributionsProps) {
    const { t } = useTranslation('report')
    const containerRef = useRef<HTMLDivElement>(null)
    const [chartHeight, setChartHeight] = useState(400)
    // Fetch product group report data
    const { data: reportData, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.PRODUCT_GROUP_REPORT,
            'services-contributions',
            filters,
        ],
        queryFn: () =>
            getProductGroupReport({
                from_date: filters.gte || '',
                to_date: filters.lte || '',
                account_manager_codes: filters.employee_id || [],
                sale_manager_codes: filters.employee_id || [],
            }),
        enabled:
            !!filters.gte &&
            !!filters.lte &&
            !!filters.employee_id &&
            filters.employee_id.length > 0,
    })

    useEffect(() => {
        if (!containerRef.current) return
        const observer = new ResizeObserver((entries) => {
            // Use requestAnimationFrame to avoid ResizeObserver loop warning
            requestAnimationFrame(() => {
                for (const entry of entries) {
                    if (entry.contentRect) {
                        setChartHeight(
                            Math.max(entry.contentRect.height * 0.8, 400)
                        )
                    }
                }
            })
        })
        observer.observe(containerRef.current)
        return () => {
            observer.disconnect()
        }
    }, [])

    // Transform report data to pie chart format
    const chartData: PieChartDataItem[] = useMemo(() => {
        if (!reportData || reportData.length === 0) {
            return []
        }

        return reportData
            .filter((item) => item.new_services > 0)
            .map((item, index) => ({
                name: item.product_group_name,
                value: item.new_services,
                color: SCHEMES.default[index % SCHEMES.default.length],
            }))
    }, [reportData])

    const hasData = chartData.length > 0

    return (
        <div ref={containerRef} className="h-full w-full">
            <Paper className="h-full flex flex-col">
                {/* Header */}
                <Typogaphy className="text-sm font-medium text-[#1A237E]">
                    {t('target_analytics.services_contribution.title')}
                </Typogaphy>

                {/* Loading/Error State */}
                {renderState()}

                {/* Empty State */}
                {!renderState() && !hasData && (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-xs text-gray-400 italic">
                            {t(
                                'target_analytics.services_contribution.filter_prompt'
                            )}
                        </p>
                    </div>
                )}

                {/* Chart Section */}
                {!renderState() && hasData && (
                    <div>
                        <PieChart
                            data={chartData}
                            height={chartHeight}
                            center={['50%', '40%']}
                            legendPosition="bottom"
                            tooltipFormatter={({ name, value, percent }) =>
                                `<div style="padding: 8px;">
                                <div style="font-weight: 600; margin-bottom: 4px;">${name}</div>
                                <div>${value} ${t(
                                    'target_analytics.services_contribution.new_services'
                                )} (${percent.toFixed(2)}%)</div>
                            </div>`
                            }
                            labelFormatter={({ name, value, percent }) =>
                                `${name}\n ({value|${value}}) {percent|${percent.toFixed(2)}%}`
                            }
                            labelRich={{
                                percent: {
                                    color: 'inherit',
                                },
                            }}
                            legendIcon="circle"
                            legendItemSize={6}
                            legendType="scroll"
                            legendGap={0}
                        />
                    </div>
                )}
            </Paper>
        </div>
    )
}
